import * as React from 'react';
import ReactDOMServer from 'react-dom/server';

import $ from 'jquery';
import type videojs from 'video.js';

import type { videojsCourseraOptions } from 'pages/open-course/video/util/video';

import type { VideoResolution } from 'bundles/asset-admin/types/assets';
import Html5Video from 'bundles/phoenix/components/HTML5Video';
import type { Html5VideoResolutionsSources, Html5VideoTracks } from 'bundles/phoenix/components/HTML5Video';

const DEFAULT_WIDTH = 680;
const DEFAULT_HEIGHT = 400;
export const OPTIMAL_RES = '540p';

const getPlayerOptions = (sources: Html5VideoResolutionsSources): videojs.PlayerOptions => {
  const playerOptions: videojs.PlayerOptions = {
    inactivityTimeout: 1000, // Hide controls bar after 1 sec of inactivity
  };

  // TODO: `sources` is usually defined at least with {}. Should this be `if (!isEmpty(sources))`?
  if (sources) {
    playerOptions.plugins = {
      resolutions: {
        sourcesByResolution: sources,
        resolution: OPTIMAL_RES,
      },
    };
  }

  return playerOptions;
};

const getVideoJsNode = (
  sources: Html5VideoResolutionsSources,
  tracks: Html5VideoTracks | undefined,
  posterUrl: string,
  options: {
    autoPlay?: boolean;
    width?: number;
    height?: number;
    defaultSubtitleLanguage?: string;
  } = {}
) => {
  return $(
    ReactDOMServer.renderToStaticMarkup(
      <Html5Video
        autoPlay={options.autoPlay || false}
        width={options.width || DEFAULT_WIDTH}
        height={options.height || DEFAULT_HEIGHT}
        sources={sources}
        tracks={tracks}
        posterUrl={posterUrl}
        defaultSubtitleLanguage={options.defaultSubtitleLanguage}
      />
    )
  ).get(0);
};

// Very similar to static/bundles/cml/legacy/types/assets.ts
export type VideoSourcesByResolution = {
  [resolution in VideoResolution]: {
    mp4VideoUrl: string;
    webMVideoUrl: string;
    previewImageUrl: string;
  };
};

export type Video = {
  sources: { byResolution: VideoSourcesByResolution };
  subtitlesVtt?: Html5VideoTracks;
  defaultSubtitleLanguage: string;
};

const getPoster = (video: Video) => {
  const videoPoster = video && video.sources.byResolution[OPTIMAL_RES].previewImageUrl;
  return videoPoster;
};

export const loadVideoJS = (
  videojsPlayerConstructor: (id: any, videojsPlayerOptions: videojs.PlayerOptions) => videojs.Player,
  video: Video,
  videoBySources: Html5VideoResolutionsSources,
  videojsCourseraOptions: videojsCourseraOptions = {}
): videojs.Player => {
  const videoPoster = getPoster(video);
  const subtitles = video.subtitlesVtt;

  const videoJsOptions: Required<Pick<videojsCourseraOptions, 'width' | 'height'>> = {
    width: videojsCourseraOptions.width || DEFAULT_WIDTH,
    height: videojsCourseraOptions.height || DEFAULT_HEIGHT,
  };

  const videoNode = getVideoJsNode(videoBySources, subtitles, videoPoster, videoJsOptions);

  const playerOptions: videojs.PlayerOptions = {
    ...getPlayerOptions(videoBySources),
    ...(videojsCourseraOptions.playerOptions || {}),
  };

  const videoJs: videojs.Player = videojsPlayerConstructor(videoNode, playerOptions);

  return videoJs;
};
